import { Alert, Box, Button, Snackbar } from '@mui/material';
import {
  PanoramaViewer,
  useAvailableLocationIds,
  useAvailableSkusGrouped,
  useAvailableStyleIds,
  useDefaultSkusGrouped,
} from '@mcmhomes/panorama-viewer';
import './panoramaViewer.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SKUs } from './types';
import { PanoramaMenu } from './PanoramaMenu';
import { useGetHomeModels, useSaveHomeDesign } from '../../api/homes';
import { HomeObjectProduct } from '../../models/homes.models';
import { useNavigate, useSearchParams } from 'react-router';
import { PanoramaCurrentSKUs } from '../../models/panorama.models';
import { HomeInquiryModal } from '../../possibly-stateful-components/HomeInquiryModal/HomeInquiryModal';
import { useGetCurrentUser } from '../../hooks/auth.ts';
import { AccountRequiredModal } from '../../components/account-required-modal/account-required-modal.tsx';
import { useTitle } from '../../hooks/useTitle.ts';

export const PanoramaViewerPage = () => {
  const SKU_GROUP_ID__LOCATIONS = '@locations';
  const SKU_GROUP_ID__STYLES = '@styles';
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();

  const homeId: string = urlSearchParams.get('homeId') || '';
  const homeVersion: string = urlSearchParams.get('homeVersion') || '';
  const host: string = urlSearchParams.get('host') || '';

  const { data: user } = useGetCurrentUser();
  const { data: HomeModelsData } = useGetHomeModels();
  const homeDesignMutation = useSaveHomeDesign();
  const homeModel = HomeModelsData?.find((model) => model.sku === homeId);

  useTitle(
    !homeModel
      ? 'Loading Customizer - Immersive Homes'
      : `Customize ${homeModel.name || 'Home Model'} - Immersive Homes`
  );

  const homeModelProducts = useMemo(() => {
    const allProducts: HomeObjectProduct[] = [];
    homeModel?.packages?.forEach((pckg) => {
      pckg.products.forEach((p) =>
        allProducts.push({ ...p, packageName: pckg.name })
      );
    });
    return allProducts;
  }, [homeModel?.packages]);

  const [locationIds] = useAvailableLocationIds({ homeId, homeVersion, host });

  const [menuOpen, setMenuOpen] = useState<boolean>(true);

  const [currentSkuIds, setCurrentSkuIds] = useState<PanoramaCurrentSKUs>(
    JSON.parse(
      urlSearchParams.get('currentSkuIds') || '{}'
    ) as PanoramaCurrentSKUs
  );

  const [savedHomeDesign, setSavedHomeDesign] = useState(false);

  const [openAccountRequiredModal, setOpenAccountRequiredModal] =
    useState(false);

  const currentLocationId: string | undefined =
    currentSkuIds[SKU_GROUP_ID__LOCATIONS];
  const currentStyleId: string | undefined =
    currentSkuIds[SKU_GROUP_ID__STYLES];

  const [styleIds] = useAvailableStyleIds({
    homeId: homeId || '',
    homeVersion: '',
    host: '',
    locationId: currentLocationId,
  });

  const [skusRaw] = useAvailableSkusGrouped({
    homeId,
    homeVersion,
    host,
    styleId: currentStyleId,
    locationId: currentLocationId,
  });

  const [skusDefault] = useDefaultSkusGrouped({
    homeId,
    homeVersion,
    host,
    styleId: currentStyleId,
    locationId: currentLocationId,
  });

  const skus: SKUs | null = useMemo(
    () =>
      !locationIds || !styleIds || !skusRaw
        ? null
        : ({
            [SKU_GROUP_ID__LOCATIONS]: locationIds ?? [],
            [SKU_GROUP_ID__STYLES]: styleIds ?? [],
            ...(skusRaw as Record<string, string[]>),
          } as SKUs),
    [styleIds, locationIds, skusRaw]
  );

  const setSkuIds = useCallback(
    (skuIds: PanoramaCurrentSKUs) => {
      setCurrentSkuIds(skuIds);
    },
    [setCurrentSkuIds]
  );

  const setStyleId = useCallback(
    (styleId: string) => {
      // Always reset to defaults when setting style, even if it's the same style
      if (skusDefault && typeof skusDefault === 'object') {
        const newSkuIds: PanoramaCurrentSKUs = {
          [SKU_GROUP_ID__LOCATIONS]:
            currentSkuIds[SKU_GROUP_ID__LOCATIONS] || '',
          [SKU_GROUP_ID__STYLES]: styleId,
        };

        // Add default SKUs for all other groups
        Object.keys(skusDefault as Record<string, string>).forEach(
          (groupId) => {
            if (
              groupId !== SKU_GROUP_ID__LOCATIONS &&
              groupId !== SKU_GROUP_ID__STYLES
            ) {
              newSkuIds[groupId as keyof PanoramaCurrentSKUs] = (
                skusDefault as Record<string, string>
              )[groupId];
            }
          }
        );

        setSkuIds(newSkuIds);
      } else {
        // If no defaults available, just update the style
        setSkuIds({ ...currentSkuIds, [SKU_GROUP_ID__STYLES]: styleId });
      }
    },
    [setSkuIds, currentSkuIds, skusDefault]
  );

  // Remove the style change effect since we handle everything in setStyleId now
  const [previousStyleId, setPreviousStyleId] = useState<string>();

  // Reset other SKUs when style changes
  useEffect(() => {
    if (
      currentStyleId !== previousStyleId &&
      currentStyleId &&
      skusDefault &&
      typeof skusDefault === 'object'
    ) {
      setPreviousStyleId(currentStyleId);

      const newSkuIds: PanoramaCurrentSKUs = { ...currentSkuIds };

      // Reset all groups except location and style to defaults
      Object.keys(skusDefault as Record<string, string>).forEach((groupId) => {
        if (
          groupId !== SKU_GROUP_ID__LOCATIONS &&
          groupId !== SKU_GROUP_ID__STYLES
        ) {
          newSkuIds[groupId as keyof PanoramaCurrentSKUs] = (
            skusDefault as Record<string, string>
          )[groupId];
        }
      });

      setSkuIds(newSkuIds);
    }
  }, [currentStyleId, skusDefault, currentSkuIds, setSkuIds, previousStyleId]);

  const setSkuIdOfGroup = useCallback(
    (groupId: string, skuId: string) =>
      setSkuIds({ ...currentSkuIds, [groupId]: skuId }),
    [setSkuIds, currentSkuIds]
  );

  const setLocationId = useCallback(
    (locationId: string) => {
      // Keep all current SKUs, just update the location
      setSkuIds({ ...currentSkuIds, [SKU_GROUP_ID__LOCATIONS]: locationId });
    },
    [setSkuIds, currentSkuIds]
  );

  const setSkuId = useCallback(
    (groupId: string, skuId: string) => {
      if (groupId === SKU_GROUP_ID__LOCATIONS) {
        setLocationId(skuId);
      } else if (groupId === SKU_GROUP_ID__STYLES) {
        setStyleId(skuId);
      } else {
        setSkuIdOfGroup(groupId, skuId);
      }
    },
    [setLocationId, setStyleId, setSkuIdOfGroup]
  );

  //defaults
  useEffect(() => {
    // if no location
    if (
      locationIds &&
      (!currentLocationId || !locationIds.includes(currentLocationId))
    ) {
      setLocationId(locationIds[0]);
    }
    // if no style
    if (styleIds && (!currentStyleId || !styleIds.includes(currentStyleId))) {
      setStyleId(styleIds[0]);
    }
  }, [
    locationIds,
    styleIds,
    currentLocationId,
    currentStyleId,
    setLocationId,
    setStyleId,
  ]);

  //navigate when changing things
  useEffect(() => {
    urlSearchParams.set('currentSkuIds', JSON.stringify(currentSkuIds));
    void navigate(
      `/panorama?currentSkuIds=${JSON.stringify(currentSkuIds)}&homeId=${homeId}`,
      { replace: true }
    );
  }, [currentSkuIds, homeId, navigate, urlSearchParams]);

  const [openSnackBar, setOpenSnackBar] = useState(false);

  const saveHomeModel = () => {
    if (user?.userId) {
      setOpenAccountRequiredModal(false);
    } else {
      setOpenAccountRequiredModal(true);
    }
    const selectedProductsArray = Object.values(currentSkuIds);
    const matchingHomeModelProducts = homeModelProducts.filter((product) =>
      selectedProductsArray.some(
        (selectedProduct) => selectedProduct === product.sku
      )
    );
    const style = {
      id: styleIds?.indexOf(currentStyleId || '') || 0,
      name: currentStyleId || '',
      products: matchingHomeModelProducts,
    };
    if (homeModel) {
      homeModel.style = style;
    }
    homeDesignMutation.mutate(
      {
        sub: user?.userId || '',
        sk: `homeDesign#${homeModel?.id.toString()}`,
        id: homeModel?.id.toString() || '',
        homeModelName: homeModel?.name || '',
        selectedProducts: homeModel ? [homeModel] : [],
        homeModelId: homeModel?.id || 0,
        customizationQuery: `/panorama?currentSkuIds=${JSON.stringify(currentSkuIds)}&homeId=${homeId}`,
        createdOn: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          setOpenSnackBar(true);
          setSavedHomeDesign(true);
        },
      }
    );
  };

  return (
    <Box className="panorama-page" data-testid="panorama-page">
      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
        <Button
          style={{
            position: 'absolute',
            top: `0px`,
            left: menuOpen ? '-100px' : '0px',
            backgroundColor: 'white',
            borderRadius: 0,
            zIndex: 1,
            color: '#002D56',
            transition: 'left 0.5s ease-in-out, opacity 0.5s ease-in-out',
            opacity: menuOpen ? 0 : 1,
          }}
          onClick={() => {
            setMenuOpen(true);
          }}
        >
          {'Open Menu'}
        </Button>
        <HomeInquiryModal homeId={homeId} savedHomeDesign={savedHomeDesign} />
        <PanoramaMenu
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          skus={skus}
          setSkuId={setSkuId}
          homeModelProducts={homeModelProducts}
          styleId={currentStyleId || ''}
          locationId={currentLocationId || ''}
          homeId={homeId}
        />
        <PanoramaViewer
          homeId={homeId}
          homeVersion={homeVersion}
          host={host}
          skus={currentSkuIds}
          locationId={currentLocationId}
          styleId={currentStyleId}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '0px',
            right: '0px',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1,
            width: 'auto',
            backgroundColor: 'white',
          }}
        >
          <Button
            disabled={homeDesignMutation.isPending}
            onClick={() => saveHomeModel()}
            variant="text"
          >
            {homeDesignMutation.isPending ? 'Saving...' : 'Save Home Design'}
          </Button>
        </Box>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackBar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackBar(false)}
        key={'snackbar'}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Home Design Added to My Portal
        </Alert>
      </Snackbar>
      {!user?.userId && openAccountRequiredModal && (
        <AccountRequiredModal
          open={openAccountRequiredModal}
          onClose={() => setOpenAccountRequiredModal(false)}
        />
      )}
    </Box>
  );
};
